<template>
  <AnnDialog
    :model-value="props.modelValue"
    @update:modelValue="emit('update:modelValue', $event)"
  >
    <div
      class="ann-congratulation"
      :class="{
        'is-text-title': !!props.titleText,
        'is-multi-row': isMultiRow,
      }"
      :style="panelStyle"
    >
      <img
        class="ann-congratulation-bg"
        :src="backgroundImg"
        alt=""
        aria-hidden="true"
      />
      <img
        v-if="!props.titleText"
        class="ann-congratulation-title"
        :src="titleImg"
        :alt="t('ann2026.congratulation.receivedReward')"
      />
      <p v-else class="ann-congratulation-title-text">{{ props.titleText }}</p>

      <div class="ann-congratulation-rewards">
        <div
          v-for="reward in displayRewards"
          :key="reward.key"
          class="ann-congratulation-reward"
        >
          <img
            class="ann-congratulation-slot"
            :src="slotImg"
            alt=""
            aria-hidden="true"
          />
          <img
            v-if="reward.icon"
            class="ann-congratulation-item"
            :src="reward.icon"
            :alt="reward.name"
          />
          <div class="ann-congratulation-name">
            <span>{{ reward.name }}</span>
            <span>×{{ reward.count }}</span>
          </div>
        </div>
      </div>

      <img
        v-if="!isMultiRow"
        class="ann-congratulation-character"
        :src="characterImg"
        alt=""
        aria-hidden="true"
      />
      <img
        v-if="!isMultiRow"
        class="ann-congratulation-confetti"
        :src="confettiImg"
        alt=""
        aria-hidden="true"
      />

      <AnnDialogClose
        :aria-label="t('ann2026.dialog.close')"
        @close="emit('update:modelValue', false)"
      />
    </div>
  </AnnDialog>
</template>

<script setup>
import { computed, defineEmits, defineProps } from 'vue'
import { useI18n } from 'vue-i18n'
import AnnDialog from '../AnnDialog/index.vue'
import AnnDialogClose from '../AnnDialogClose/index.vue'
import { getItemIcon, isItemId } from '../../item'
import {
  getItemName,
  getRewardCount,
  getRewardId,
  parseProps,
} from '../../utils/reward'
import backgroundImg from '@/assets/imgs/act/ann2026/popups/congratulation/background.png'
import titleZh from '@/assets/imgs/act/ann2026/popups/congratulation/title/zh.png'
import titleEn from '@/assets/imgs/act/ann2026/popups/congratulation/title/en.png'
import titleVi from '@/assets/imgs/act/ann2026/popups/congratulation/title/vi.png'
import titleId from '@/assets/imgs/act/ann2026/popups/congratulation/title/id.png'
import titleTh from '@/assets/imgs/act/ann2026/popups/congratulation/title/th.png'
import slotImg from '@/assets/imgs/act/ann2026/popups/congratulation/reward-slot.png'
import characterImg from '@/assets/imgs/act/ann2026/popups/congratulation/character.png'
import confettiImg from '@/assets/imgs/act/ann2026/popups/congratulation/confetti.png'

const props = defineProps({
  modelValue: {
    type: Boolean,
    default: false,
  },
  rewards: {
    type: Array,
    default: () => [],
  },
  // 有值时用文字标题，替代多语言切图（如任务领火力：恭喜您獲得獎勵）
  titleText: {
    type: String,
    default: '',
  },
})

const emit = defineEmits(['update:modelValue'])

const { t, te, locale } = useI18n({ useScope: 'global' })

const titleImg = computed(() => {
  switch (locale.value) {
    case 'hk':
    case 'tw':
      return titleZh
    case 'vi':
      return titleVi
    case 'id':
      return titleId
    case 'th':
      return titleTh
    default:
      return titleEn
  }
})

const rewardItems = computed(() =>
  props.rewards.flatMap((reward) => {
    const snapshot = parseProps(reward.propSnapshot)
    return snapshot.length ? snapshot : [reward]
  }),
)

const displayRewards = computed(() =>
  rewardItems.value.map((reward, index) => {
    const id = getRewardId(reward)
    return {
      key: `${id || 'reward'}-${index}`,
      name:
        reward.name ||
        reward.itemName ||
        reward.rewardName ||
        getItemName(id, t, te) ||
        '',
      count: getRewardCount(reward),
      icon: reward.icon || (isItemId(id) ? getItemIcon(id) : null),
    }
  }),
)

const isMultiRow = computed(() => displayRewards.value.length > 3)

const rem = (px) => `${+(px / 75.2).toFixed(5)}rem`

const panelStyle = computed(() => {
  const rows = Math.max(1, Math.ceil(displayRewards.value.length / 3))
  if (rows <= 1) return { height: rem(505) }
  // 多行：含名称两行高度 + 底框留白，避免文案贴出白底
  const height = 115 + rows * 215 + 64
  return { height: rem(height) }
})
</script>

<style lang="less" scoped>
.ann-congratulation {
  --ann-close-top: -50px;
  --ann-close-right: -19px;

  position: relative;
  width: 639px;
  height: 505px;
  overflow: visible;
}

.ann-congratulation-bg {
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  pointer-events: none;
  object-fit: fill;
}

.ann-congratulation-title {
  position: absolute;
  top: 0px;
  left: 50%;
  z-index: 3;
  display: block;
  max-width: calc(100% - 40px);
  object-fit: contain;
  transform: translateX(-50%);
  height: 100px;
}

// 文字标题落在白底标题区（切图标题会探出边框，文字需留白）
.ann-congratulation-title-text {
  position: absolute;
  top: 78px;
  left: 50%;
  z-index: 3;
  width: calc(100% - 100px);
  margin: 0;
  color: #273271;
  font-size: 30px;
  line-height: 42px;
  font-weight: 900;
  text-align: center;
  transform: translateX(-50%);
  white-space: nowrap;
}

.ann-congratulation-rewards {
  position: absolute;
  top: 145px;
  left: 50%;
  z-index: 2;
  display: flex;
  flex-wrap: wrap;
  justify-content: center;
  align-content: flex-start;
  gap: 26px;
  width: 530px;
  transform: translateX(-50%);
}

// 单行：在标题与底部角色之间垂直居中
.ann-congratulation:not(.is-multi-row) .ann-congratulation-rewards {
  top: 130px;
  height: 210px;
  align-content: center;
}

.ann-congratulation.is-text-title:not(.is-multi-row) .ann-congratulation-rewards {
  top: 150px;
  height: 200px;
}

.ann-congratulation.is-multi-row .ann-congratulation-rewards {
  top: 115px;
  height: auto;
  padding-bottom: 28px;
  box-sizing: content-box;
  align-content: flex-start;
  row-gap: 28px;
}

.ann-congratulation-reward {
  position: relative;
  flex: 0 0 149px;
  width: 149px;
  height: 216px;
}

.ann-congratulation.is-multi-row .ann-congratulation-reward {
  height: 215px;
}

.ann-congratulation-slot {
  position: absolute;
  top: 0;
  left: 0;
  width: 149px;
  height: 149px;
}

.ann-congratulation-item {
  position: absolute;
  top: 22px;
  left: 22px;
  width: 105px;
  height: 105px;
  object-fit: contain;
}

.ann-congratulation-name {
  position: absolute;
  top: 152px;
  left: 50%;
  z-index: 2;
  display: -webkit-box;
  width: 164px;
  height: 60px;
  overflow: hidden;
  color: #273271;
  font-size: 15px;
  line-height: 20px;
  font-weight: 700;
  text-align: center;
  white-space: normal;
  overflow-wrap: anywhere;
  word-break: break-word;
  transform: translateX(-50%);
  -webkit-line-clamp: 3;
  -webkit-box-orient: vertical;

  span {
    display: inline;
  }

  span + span {
    margin-left: 2px;
  }
}

.ann-congratulation.is-multi-row .ann-congratulation-name {
  top: 148px;
  line-height: 18px;
}

.ann-congratulation-character {
  position: absolute;
  top: 332px;
  left: 417px;
  z-index: 1;
  width: 190px;
  height: 172px;
  pointer-events: none;
}

.ann-congratulation-confetti {
  position: absolute;
  top: 305px;
  left: 527px;
  z-index: 1;
  width: 168px;
  height: 195px;
  pointer-events: none;
}
</style>
